import React, { useMemo, useCallback, useContext } from 'react';
import { Checkbox, Space } from 'antd';
import type { CheckboxProps as AntCheckboxProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { mergeStoreProps } from '../../base/storePropsUtils';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { useDictOptions, useRemoteOptions } from '../../base/hooks/useApiService';
import { findDictOptionByValue } from '../../config';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';
import { deriveScopedComponentId } from '../../base/storePropsUtils';

interface CheckboxOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

// 选项配置接口
interface OptionsCfg {
  url?: string;
  codeKey?: string;
  labelKey?: string;
}

/**
 * AndCheckbox Props 接口
 */
export interface AndCheckboxProps extends Omit<AntCheckboxProps, 'onChange'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  /** 选项列表 (用于多选模式) */
  options?: CheckboxOption[];
  /** 远程选项配置 */
  optionsCfg?: OptionsCfg;
  /** 是否为多选模式 */
  multiple?: boolean;
  /** 变更动作 */
  changeAction?: string;
  /** 校验规则 */
  rules?: any[];
  /** 失焦时校验 */
  validateOnBlur?: boolean;
  /** 修改时校验 */
  validateOnChange?: boolean;
  /** 自定义变更处理函数 (多选) */
  onChangeMultiple?: (values: (string | number)[]) => void;
  /** 自定义变更处理函数 (单选) */
  onChange?: (checked: boolean) => void;
  /** 多选值 */
  values?: (string | number)[];
  /** 字典编码 */
  dictCode?: string;
  /** 远程数据URL（自定义属性） */
  remoteUrl?: string;
  /** 排列方向 */
  direction?: 'horizontal' | 'vertical';
  /** 迭代器内当前行序号，供 propsStore 隔离与动作参数使用 */
  index?: number;
}

/**
 * AndCheckbox 组件
 * 封装 Ant Design Checkbox，支持单选和多选模式
 */
export const AndCheckbox: React.FC<AndCheckboxProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  options = [],
  optionsCfg,
  multiple = false,
  changeAction,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  onChange,
  onChangeMultiple,
  checked: propChecked,
  values: propValues,
  defaultChecked,
  children,
  dictCode,
  index,
  direction = 'horizontal',
  // 解构自定义属性防止传递到 DOM
  remoteUrl: _remoteUrl,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;

  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );
  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storeProps = useAndStore((s) =>
    derivedPropsComponentId ? s.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField ? s.getDataSet(dataSetKey, effectiveDataSetField) : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet: effectiveDataSet,
    dataSetField: effectiveDataSetField,
    sessionId: effectiveSessionId,
    id,
    develop,
  });

  // 使用 dictCode 加载字典数据
  const { options: dictOptions } = useDictOptions(dictCode);

  // 使用 optionsCfg 加载远程数据
  const { options: remoteOptions } = useRemoteOptions(
    optionsCfg?.url,
    {
      valueKey: optionsCfg?.codeKey || 'value',
      labelKey: optionsCfg?.labelKey || 'label',
      immediate: !!optionsCfg?.url,
    }
  );

  // 单选模式的值
  const displayChecked = useMemo(() => {
    if (!multiple) {
      if (storeValue !== undefined) {
        return Boolean(storeValue);
      }
      if (storeProps?.checked !== undefined) {
        return storeProps.checked;
      }
      return propChecked ?? defaultChecked;
    }
    return undefined;
  }, [multiple, storeValue, storeProps?.checked, propChecked, defaultChecked]);

  // 最终选项
  const finalOptions = useMemo<CheckboxOption[]>(() => {
    // 优先级: Store > 远程 > 字典 > Props
    if (storeProps?.options) return storeProps.options;
    if (optionsCfg?.url && remoteOptions.length > 0) return remoteOptions;
    if (dictCode && dictOptions.length > 0) return dictOptions;
    return options;
  }, [storeProps?.options, optionsCfg?.url, remoteOptions, dictCode, dictOptions, options]);

  // 多选模式的值（字典 value 多为 string，实际数据可能为 number，需用选项中的 value 以保证 antd Checkbox 正确选中）
  const displayValues = useMemo<(string | number)[]>(() => {
    if (!multiple) return [];
    let raw: (string | number)[] = [];
    if (Array.isArray(storeValue)) raw = storeValue;
    else if (Array.isArray(storeProps?.values)) raw = storeProps.values;
    else raw = propValues || [];
    if (finalOptions.length === 0) return raw;
    return raw.map((v) => (findDictOptionByValue(finalOptions, v)?.value as string | number) ?? v);
  }, [multiple, storeValue, storeProps?.values, propValues, finalOptions]);

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined, { skipKeys: ['value', 'help', 'validateStatus', 'checked', 'values', 'options'] }),
    [restProps, storeProps]
  );
  const checkboxDomProps = useMemo(
    () => prepareAntdDomProps(mergedProps as Record<string, unknown>),
    [mergedProps],
  );
  const mergedDirection = (mergedProps.direction ?? direction) as 'horizontal' | 'vertical';
  const orientation = mergedDirection === 'vertical' ? 'vertical' : 'horizontal';

  // 单选变更处理
  const handleSingleChange = useCallback((e: any) => {
    const checked = e.target.checked;

    if (onChange) {
      onChange(checked);
      return;
    }

    if (dataSetKey && effectiveDataSetField) {
      setDataSet(dataSetKey, effectiveDataSetField, checked);
    }

    if (validateOnChange) validate(checked);

    if (changeAction && effectiveSessionId) {
      const { actionName, arg } = parseChangeAction(changeAction, checked, {
        id,
        dataSet: effectiveDataSet,
        dataSetField: effectiveDataSetField,
      }, ctx);
      const nextArg =
        typeof arg === 'object' && arg !== null
          ? { ...arg, index: effectiveIndex, parentIndex: effectiveParentIndex }
          : arg;
      addCommand(effectiveSessionId, createCommand(actionName, nextArg));
    }
  }, [
    onChange,
    dataSetKey,
    effectiveDataSetField,
    setDataSet,
    validateOnChange,
    validate,
    changeAction,
    effectiveSessionId,
    id,
    addCommand,
    effectiveIndex,
    effectiveParentIndex,
    effectiveDataSet,
    ctx,
  ]);

  // 多选变更处理
  const handleMultipleChange = useCallback((checkedValues: (string | number)[]) => {
    if (onChangeMultiple) {
      onChangeMultiple(checkedValues);
      return;
    }

    if (dataSetKey && effectiveDataSetField) {
      setDataSet(dataSetKey, effectiveDataSetField, checkedValues);
    }

    if (validateOnChange) validate(checkedValues);

    if (changeAction && effectiveSessionId) {
      const { actionName, arg } = parseChangeAction(changeAction, checkedValues, {
        id,
        dataSet: effectiveDataSet,
        dataSetField: effectiveDataSetField,
      }, ctx);
      const nextArg =
        typeof arg === 'object' && arg !== null
          ? { ...arg, index: effectiveIndex, parentIndex: effectiveParentIndex }
          : arg;
      addCommand(effectiveSessionId, createCommand(actionName, nextArg));
    }
  }, [
    onChangeMultiple,
    dataSetKey,
    effectiveDataSetField,
    setDataSet,
    validateOnChange,
    validate,
    changeAction,
    effectiveSessionId,
    id,
    addCommand,
    effectiveIndex,
    effectiveParentIndex,
    effectiveDataSet,
    ctx,
  ]);

  const handleBlur = useCallback(() => {
    if (!validateOnBlur) return;
    if (multiple) validate(displayValues);
    else validate(displayChecked);
  }, [validateOnBlur, validate, multiple, displayValues, displayChecked]);

  // 多选模式
  if (multiple && finalOptions.length > 0) {
    const { indeterminate: _indeterminate, direction: _direction, ...groupProps } = checkboxDomProps as Record<string, any>;
    return (
      <div onBlur={handleBlur}>
        <Checkbox.Group {...groupProps} value={displayValues} onChange={handleMultipleChange} disabled={develop ? false : (checkboxDomProps.disabled as boolean | undefined)}>
          <Space
            orientation={orientation}
            wrap
          >
            {finalOptions.map((option) => (
              <Checkbox key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
        <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
      </div>
    );
  }

  // 单选模式
  return (
    <div onBlur={handleBlur}>
      <Checkbox
        {...checkboxDomProps}
        disabled={develop ? false : (checkboxDomProps.disabled as boolean | undefined)}
        checked={displayChecked}
        onChange={handleSingleChange}
      >
        {children}
      </Checkbox>
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndCheckbox;
